import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { api } from '../services/api';

interface CategorySummary {
  categoryId: string;
  categoryName: string;
  totalAmountEur: number;
  transactionCount: number;
}

interface Summary {
  month: string;
  totalIncome: number;
  totalExpense: number;
  netAmount: number;
  byCategory: CategorySummary[];
}

const COLORS = [
  '#1976d2', '#ff9800', '#4caf50', '#f44336', '#9c27b0',
  '#00bcd4', '#ff5722', '#607d8b', '#e91e63', '#3f51b5',
  '#009688', '#795548',
];

export default function ReportsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [month, setMonth] = useState(new Date().toISOString().substring(0, 7));
  const [summary, setSummary] = useState<Summary | null>(null);

  const formatEur = (amount: number) =>
    new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(amount);

  useEffect(() => {
    api.get<Summary>(`/api/reports/summary?month=${month}`).then(setSummary).catch(() => {});
  }, [month]);

  const expenseCategories = (summary?.byCategory || [])
    .filter((c) => c.totalAmountEur < 0)
    .map((c) => ({ ...c, amount: Math.abs(c.totalAmountEur) }))
    .sort((a, b) => b.amount - a.amount);

  const pieData = expenseCategories.map((c) => ({
    name: c.categoryName,
    value: c.amount,
  }));

  const barData = [
    { name: 'Tulot', amount: summary?.totalIncome || 0 },
    { name: 'Menot', amount: Math.abs(summary?.totalExpense || 0) },
    { name: 'Netto', amount: summary?.netAmount || 0 },
  ];

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
          Raportit
        </Typography>
        <TextField
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        />
      </Box>

      {/* Income vs Expense bar chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tulot vs. Menot
          </Typography>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barData}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value: number) => formatEur(value)} />
              <Bar dataKey="amount" fill="#1976d2">
                {barData.map((_, idx) => (
                  <Cell
                    key={idx}
                    fill={idx === 0 ? '#4caf50' : idx === 1 ? '#f44336' : '#1976d2'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Spending by category pie chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Menot kategorioittain
          </Typography>
          {pieData.length === 0 ? (
            <Typography color="text.secondary">Ei menotietoja</Typography>
          ) : (
            <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems="center">
              <ResponsiveContainer width={isMobile ? '100%' : '50%'} height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {pieData.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatEur(value)} />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ flex: 1, pl: isMobile ? 0 : 2 }}>
                {expenseCategories.map((cat, idx) => (
                  <Box
                    key={cat.categoryId}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    py={0.5}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: COLORS[idx % COLORS.length],
                        }}
                      />
                      <Typography variant="body2">{cat.categoryName}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      {formatEur(cat.amount)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
